import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import { mutate as mutateGlobal } from "swr";
import * as Sentry from "@sentry/react";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { useApiClient } from "@/hooks/useApiClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import {
  FetchAllRegistryChangeAlertsAPI,
  FetchRegistryChangeAlertsByIdsAPI,
  GetRegistryChangeAlertsAPI,
  MarkRegistryChangeAlertsReadAPI,
  PostReuseAcquireAPI,
  REGISTRY_CHANGE_ALERT_SUMMARY_ENDPOINT,
  UpdateRegistryChangeAlertStateAPI,
} from "@/features/registryChangeAlert/api";
import { RegistryChangeAlertResponsiveList } from "@/features/registryChangeAlert/components/RegistryChangeAlertResponsiveList";
import { RegistryChangeAlertDataGrid } from "@/features/registryChangeAlert/components/RegistryChangeAlertDataGrid";
import { ReuseAcquireConfirmDialog } from "@/features/registryChangeAlert/components/ReuseAcquireConfirmDialog";
import {
  buildReuseAcquireRequest,
  canBuildReuseAcquireItem,
  chunkAlerts,
  countByPictureType,
} from "@/features/registryChangeAlert/utils/buildReuseAcquireRequest";
import {
  DEFAULT_PAGE_SIZE,
  MAX_ITEMS_PER_ACQUIRE_REQUEST,
  PICTURE_TYPE_FILTER_ALL,
  PICTURE_TYPE_FILTER_OPTIONS,
  REGISTRY_CHANGE_ALERT_ACQUIRE_NOTE,
  REGISTRY_CHANGE_ALERT_DESCRIPTION,
  REGISTRY_CHANGE_ALERT_TITLE,
} from "@/features/registryChangeAlert/utils/constants";
import type { RegistryChangeAlertEntity } from "@/features/registryChangeAlert/types";

/** 取得依頼 API のエラーから画面に出す文言を得る。 */
const toAcquireErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (error.response?.status === HttpStatusCode.Forbidden) {
      return READ_ONLY_TOOLTIP_MESSAGE;
    }
    const data = error.response?.data as
      | { nonFieldErrors?: string[] }
      | undefined;
    if (data?.nonFieldErrors?.length) {
      return data.nonFieldErrors.join("\n");
    }
  }
  return "リユース取得の依頼に失敗しました。時間をおいて再度お試しください。";
};

/**
 * 依頼がサーバーで受け付けられたか分からない失敗か（応答が無い・サーバーエラー）。
 * この場合は再送すると二重に取得・課金され得るため、再送させずに取得済み登記一覧で状態を確かめてもらう。
 */
const isOutcomeUnknown = (error: unknown): boolean =>
  !isAxiosError(error) ||
  error.response === undefined ||
  error.response.status >= HttpStatusCode.InternalServerError;

/** 結果が分からない失敗のときの案内。 */
const OUTCOME_UNKNOWN_MESSAGE =
  "依頼の結果を確認できませんでした。二重に取得しないよう、取得済み登記の一覧で依頼の状況をご確認ください。";

/**
 * 取得済み登記の変動アラート画面。
 * 選んだ物件（ページ跨ぎ可・全件も可）の新しい登記を、確認モーダルからそのままリユース取得する。
 * 新規取得は行わず、依頼後は取得済み登記の一覧へ移る。
 */
export const RegistryChangeAlerts: React.FC = () => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();

  const [pictureType, setPictureType] = useState(PICTURE_TYPE_FILTER_ALL);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  // 確認モーダルで取得する対象（null は閉じている）。
  const [acquireTargets, setAcquireTargets] = useState<
    RegistryChangeAlertEntity[] | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 課金を伴う依頼の二重送信を防ぐ（ボタンのタイムアウト後の再クリックでも送らない）。
  const submittingRef = useRef(false);
  // 既読にしたアラート ID（同じ画面で重ねて送らない）。
  const markedReadIdsRef = useRef(new Set<number>());
  const [isFetchingAll, setIsFetchingAll] = useState(false);

  const pictureTypeParam =
    pictureType === PICTURE_TYPE_FILTER_ALL ? undefined : pictureType;
  const request = useMemo(
    () => ({ pictureType: pictureTypeParam, page: page + 1, pageSize }),
    [pictureTypeParam, page, pageSize]
  );
  const { data, isLoading, error, mutate } = GetRegistryChangeAlertsAPI(
    apiClient,
    request
  );

  // 表示したアラートは既読にする。この画面の未読表示はそのまま残し、次回表示・要約から反映する。
  useEffect(() => {
    const unreadIds = (data?.items ?? [])
      .filter(
        (alert) => !alert.isRead && !markedReadIdsRef.current.has(alert.id)
      )
      .map((alert) => alert.id);
    if (unreadIds.length === 0) return;
    unreadIds.forEach((id) => markedReadIdsRef.current.add(id));
    MarkRegistryChangeAlertsReadAPI(apiClient, unreadIds)
      .then(() => {
        void mutateGlobal(REGISTRY_CHANGE_ALERT_SUMMARY_ENDPOINT);
      })
      .catch(() => {
        unreadIds.forEach((id) => markedReadIdsRef.current.delete(id));
      });
  }, [apiClient, data]);

  // 取得ボタンを続けて押したとき、先に押した操作の応答が後から届いて確認対象を上書きしないよう、
  // 最後に押した操作の応答だけを使う。絞り込みを変えたときも、進行中の取得を無効にする。
  const openAcquireRequestIdRef = useRef(0);

  const handlePictureTypeChange = useCallback((value: string): void => {
    openAcquireRequestIdRef.current += 1;
    setIsFetchingAll(false);
    setAcquireTargets(null);
    setPictureType(value);
    setPage(0);
    setSelectionModel([]);
  }, []);

  const handlePageSizeChange = useCallback((value: number): void => {
    setPage(0);
    setPageSize(value);
  }, []);

  const handleSelectionModelChange = useCallback(
    (model: GridSelectionModel): void => {
      setSelectionModel(model);
    },
    []
  );

  // 表示中・選択時の内容は古くなり得る（夜間同期で新しい謄本が替わる・非表示や依頼済みで消える）ため、
  // どの取得操作でも確認モーダルを開く直前に最新の一覧を読み直し、今も表示対象の分だけを最新の内容で取得する。
  const openAcquireDialog = useCallback(
    async (ids: ReadonlyArray<string | number> | "all"): Promise<void> => {
      openAcquireRequestIdRef.current += 1;
      const requestId = openAcquireRequestIdRef.current;
      setIsFetchingAll(true);
      try {
        // すべて取得のときだけ全件を読み、選んだ物件は ID を指定して今も表示対象の分だけを読み直す。
        const latest =
          ids === "all"
            ? await FetchAllRegistryChangeAlertsAPI(apiClient, pictureTypeParam)
            : await FetchRegistryChangeAlertsByIdsAPI(
                apiClient,
                ids.map(Number)
              );
        if (requestId !== openAcquireRequestIdRef.current) return;
        const latestById = new Map(
          latest.map((alert) => [String(alert.id), alert])
        );
        const latestTargets =
          ids === "all"
            ? latest
            : ids
                .map((id) => latestById.get(String(id)))
                .filter(
                  (alert): alert is RegistryChangeAlertEntity =>
                    alert !== undefined
                );
        // 都道府県や不動産種別を特定できない行は、1 件のために依頼全体が失敗しないよう対象から外す。
        const targets = latestTargets.filter(canBuildReuseAcquireItem);
        const isSelectionReduced =
          ids !== "all" && latestTargets.length < ids.length;
        // 全件が消えたときは、下の「対象の物件は…」だけを出して通知を重ねない。
        if (isSelectionReduced && latestTargets.length > 0) {
          toast.info(
            `${(
              ids.length - latestTargets.length
            ).toLocaleString()}件は取得済み・非表示、または一覧の更新により対象外となったため、除きました`
          );
        }
        if (targets.length < latestTargets.length) {
          toast.warning(
            `${(
              latestTargets.length - targets.length
            ).toLocaleString()}件は所在地または種別を特定できないため、対象から除きました`
          );
        }
        if (targets.length === 0) {
          if (latestTargets.length === 0) {
            toast.info(
              "対象の物件は、すでに取得済み・非表示、または一覧の更新により対象外になっています"
            );
          }
          void mutate();
          return;
        }
        // 一覧に残った行と選択を、最新の状態に合わせ直す。
        if (isSelectionReduced || targets.length < latestTargets.length) {
          void mutate();
        }
        setAcquireTargets(targets);
      } catch {
        if (requestId === openAcquireRequestIdRef.current) {
          toast.error("変動アラートの取得に失敗しました");
        }
      } finally {
        if (requestId === openAcquireRequestIdRef.current) {
          setIsFetchingAll(false);
        }
      }
    },
    [apiClient, mutate, pictureTypeParam]
  );

  const handleAcquireSelected = useCallback((): void => {
    void openAcquireDialog(selectionModel);
  }, [openAcquireDialog, selectionModel]);

  const handleAcquireAll = useCallback((): void => {
    void openAcquireDialog("all");
  }, [openAcquireDialog]);

  const handleAcquireRow = useCallback(
    (alert: RegistryChangeAlertEntity): void => {
      void openAcquireDialog([alert.id]);
    },
    [openAcquireDialog]
  );

  // 依頼 API の上限ごとに分けて順に送る。途中で失敗したら、依頼済みの分は取得済み登記一覧で確認できるようにする。
  const handleConfirm = useCallback(
    async (cancelLoading: () => void): Promise<void> => {
      if (acquireTargets === null || acquireTargets.length === 0) return;
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);
      let requestedCount = 0;
      try {
        for (const chunk of chunkAlerts(
          acquireTargets,
          MAX_ITEMS_PER_ACQUIRE_REQUEST
        )) {
          await PostReuseAcquireAPI(
            apiClient,
            buildReuseAcquireRequest(chunk, REGISTRY_CHANGE_ALERT_TITLE)
          );
          requestedCount += chunk.length;
        }
        navigate("/acquirebooklist");
      } catch (e) {
        if (isOutcomeUnknown(e)) {
          Sentry.captureException(e);
          toast.error(
            requestedCount > 0
              ? `${requestedCount.toLocaleString()}件は依頼しました。${OUTCOME_UNKNOWN_MESSAGE}`
              : OUTCOME_UNKNOWN_MESSAGE
          );
          navigate("/acquirebooklist");
          return;
        }
        if (requestedCount > 0) {
          // 入力エラーなど再送できる失敗は、依頼済みの分を除いた残りをモーダルに残して再送できるようにする。
          toast.error(
            `${requestedCount.toLocaleString()}件は依頼しました。残り${(
              acquireTargets.length - requestedCount
            ).toLocaleString()}件の依頼に失敗しました。${toAcquireErrorMessage(
              e
            )}`
          );
          setAcquireTargets(acquireTargets.slice(requestedCount));
          // 依頼済みの行が一覧に残って再依頼されないよう、一覧を読み直す。
          void mutate();
          cancelLoading();
          return;
        }
        toast.error(toAcquireErrorMessage(e));
        cancelLoading();
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [acquireTargets, apiClient, mutate, navigate]
  );

  const handleDismiss = useCallback(
    async (alert: RegistryChangeAlertEntity): Promise<void> => {
      try {
        await UpdateRegistryChangeAlertStateAPI(apiClient, alert.id, {
          isDismissed: true,
        });
        // 続けて非表示にした場合に古い選択状態で上書きしないよう、最新の状態から取り除く。
        const dismissedId = String(alert.id);
        setSelectionModel((prev) =>
          prev.filter((id) => String(id) !== dismissedId)
        );
        toast.success("アラートを非表示にしました");
        void mutate();
      } catch {
        toast.error("アラートの非表示に失敗しました");
      }
    },
    [apiClient, mutate]
  );

  const handleDismissClick = useCallback(
    (alert: RegistryChangeAlertEntity): void => {
      void handleDismiss(alert);
    },
    [handleDismiss]
  );

  const total = data?.total ?? 0;
  // 最終ページの最後の1件を非表示にするなど、件数が減って現在のページが範囲外になったときは最終ページへ詰める。
  // PC の DataGrid は自動で詰めるが、SP の TablePagination は詰めないため親で補正する。
  useEffect(() => {
    if (!data || page === 0 || page * pageSize < data.total) return;
    setPage(Math.max(0, Math.ceil(data.total / pageSize) - 1));
  }, [data, page, pageSize]);
  const isMobile = useIsMobile();
  // SP は表形式にせずカード（ResponsiveTable）で出す。
  const AlertList = isMobile
    ? RegistryChangeAlertResponsiveList
    : RegistryChangeAlertDataGrid;

  const counts = useMemo(
    () => countByPictureType(acquireTargets ?? []),
    [acquireTargets]
  );

  return (
    <PagePaper>
      <Box sx={{ display: "flex", mb: 1 }}>
        <NotificationsActiveOutlinedIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>{REGISTRY_CHANGE_ALERT_TITLE}</PageTitle>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {REGISTRY_CHANGE_ALERT_DESCRIPTION}
      </Typography>

      <SectionPaper>
        <Box sx={{ p: { xs: 2.5, [MOBILE_BREAKPOINT]: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1.5}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <TextField
                select
                size="small"
                label="登記の種類"
                value={pictureType}
                onChange={(event) => {
                  handlePictureTypeChange(event.target.value);
                }}
                sx={{ minWidth: 160 }}
              >
                {PICTURE_TYPE_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {error == null && (
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                  {total.toLocaleString()} 件
                </Typography>
              )}
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                disabled={total === 0 || isFetchingAll}
                onClick={handleAcquireAll}
                sx={{
                  fontWeight: 600,
                  minHeight: {
                    xs: MIN_TAP_TARGET,
                    [MOBILE_BREAKPOINT]: "auto",
                  },
                }}
              >
                すべてリユース取得
                {total > 0 && `（${total.toLocaleString()}件）`}
              </Button>
              <Button
                variant="contained"
                size="small"
                disableElevation
                startIcon={<AddHomeWorkOutlinedIcon />}
                disabled={selectionModel.length === 0 || isFetchingAll}
                onClick={handleAcquireSelected}
                sx={{
                  fontWeight: 600,
                  minHeight: {
                    xs: MIN_TAP_TARGET,
                    [MOBILE_BREAKPOINT]: "auto",
                  },
                }}
              >
                選択した物件をまとめてリユース取得
                {selectionModel.length > 0 && `（${selectionModel.length}件）`}
              </Button>
            </Stack>
          </Stack>

          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1.5, color: "text.secondary" }}
          >
            {REGISTRY_CHANGE_ALERT_ACQUIRE_NOTE}
          </Typography>

          {error != null ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    void mutate();
                  }}
                  sx={{
                    minHeight: {
                      xs: MIN_TAP_TARGET,
                      [MOBILE_BREAKPOINT]: "auto",
                    },
                  }}
                >
                  再試行
                </Button>
              }
            >
              変動アラートの取得に失敗しました。時間をおいて再度お試しください。
            </Alert>
          ) : (
            <AlertList
              alerts={data?.items ?? []}
              rowCount={total}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              selectionModel={selectionModel}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              onSelectionModelChange={handleSelectionModelChange}
              onAcquire={handleAcquireRow}
              onDismiss={handleDismissClick}
              isReadOnly={isReadOnly}
            />
          )}
        </Box>
      </SectionPaper>

      <ReuseAcquireConfirmDialog
        open={acquireTargets !== null}
        counts={counts}
        isReadOnly={isReadOnly}
        disableCancel={isSubmitting}
        onCancel={() => {
          setAcquireTargets(null);
        }}
        onConfirm={(cancelLoading) => {
          void handleConfirm(cancelLoading);
        }}
      />
    </PagePaper>
  );
};
